import type { RegisterPage } from '@edwin-group/cj-graphql/generated/graphql';
import { getRegisterPage, register as registerUser } from '@/services/auth/auth.service';
import { useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useToast } from '@/components/ui/use-toast';
import { z } from 'zod';
import { useParams } from 'react-router-dom';
import useLogin from '../login/uesLogin';
import { trackGAEvent } from '@/lib/ga-custom-events/dispatcher';
// import { usePreFetchImage } from '@/hooks/usePreFetchImage';

const registrationSchema = z
  .object({
    surname: z.string().min(1, { message: 'This field is required' }),
    forename: z.string().min(1, { message: 'This field is required' }),
    email: z.string().email({ message: 'Invalid email address' }),
    schoolCode: z.string().min(6).max(6, { message: 'School code must be 6 characters long' }),
    password: z.string().min(8, { message: 'Password must be at least 8 characters' }),
    confirmPassword: z.string().min(8, { message: 'Password must be at least 8 characters' }),
    termsAndConditions: z.literal<boolean>(true, {
      errorMap: () => ({ message: 'To continue, you must accept the terms and conditions' }),
    }),
  })
  .superRefine(({ confirmPassword, password }, ctx) => {
    if (confirmPassword !== password) {
      ctx.addIssue({
        path: ['confirmPassword'],
        code: 'custom',
        message: 'The passwords did not match',
      });
    }
  });

export type FormInputs = z.infer<typeof registrationSchema>;

export default function useRegister() {
  const [registerPage, setRegisterPage] = useState<RegisterPage | null>(null);
  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    resolver: zodResolver(
      z
        .object({
          surname: z
            .string()
            .min(1, { message: registerPage?.fieldErrors?.surnameRequired || 'This field is required' }),
          forename: z
            .string()
            .min(1, { message: registerPage?.fieldErrors?.forenameRequired || 'This field is required' }),
          email: z.string().email({ message: registerPage?.fieldErrors?.emailRequired || 'Invalid email address' }),
          schoolCode: z
            .string()
            .min(6, {
              message: registerPage?.fieldErrors?.schoolCodeRequired || 'School code must be 6 characters long',
            })
            .max(6, {
              message: registerPage?.fieldErrors?.schoolCodeRequired || 'School code must be 6 characters long',
            }),
          password: z.string().min(8, {
            message: registerPage?.fieldErrors?.passwordRequired || 'Password must be at least 8 characters',
          }),
          confirmPassword: z.string().min(8, {
            message: registerPage?.fieldErrors?.confirmPasswordRequired || 'Password must be at least 8 characters',
          }),
          termsAndConditions: z.literal<boolean>(true, {
            errorMap: () => ({
              message:
                registerPage?.fieldErrors?.termsAndConditionsRequired ||
                'To continue, you must accept the terms and conditions',
            }),
          }),
        })
        .superRefine(({ confirmPassword, password }, ctx) => {
          if (confirmPassword !== password) {
            ctx.addIssue({
              path: ['confirmPassword'],
              code: 'custom',
              message: registerPage?.fieldErrors?.confirmPasswordDidNotMatch || 'The passwords did not match',
            });
          }
        }),
    ),
    mode: 'onBlur',
  });
  const { toast } = useToast();
  const { code } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const { onLogin } = useLogin();

  const formRef = useRef<HTMLFormElement | null>(null);

  const onSubmit: SubmitHandler<FormInputs> = async (user) => {
    setIsLoading(true);
    const { error } = await registerUser({
      email: user.email,
      password: user.password,
      schoolCode: user.schoolCode,
      forename: user.forename,
      surname: user.surname,
    });

    if (error) {
      toast({
        title: registerPage?.popUpMessages?.error?.title || 'Error registering user',
        description: registerPage?.popUpMessages?.error?.message || error.message,
        variant: 'destructive',
      });
      setIsLoading(false);
      return;
    }

    await new Promise((resolve) => setTimeout(resolve, 3000));

    trackGAEvent('new_registration', {
      registration_code: user.schoolCode,
    });

    await onLogin({
      ...user,
      identifier: user.email,
      errorMessage: registerPage?.popUpMessages?.error,
    });

    setIsLoading(false);

    toast({
      title: registerPage?.popUpMessages?.success?.title || 'User registered',
      description: registerPage?.popUpMessages?.success?.message || 'User has been registered successfully!',
    });
  };

  const handleSubmitForm = () => {
    if (formRef.current) formRef.current.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  };

  useEffect(() => {
    (async () => {
      const [registerPageResponse] = await Promise.all([getRegisterPage()]);
      setRegisterPage(registerPageResponse.page as RegisterPage);
    })();
  }, []);

  useEffect(() => {
    if (code) {
      reset({ schoolCode: code });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  // const { isImageLoaded } = usePreFetchImage(registerPage?.pageImage.data?.attributes?.url);

  return {
    errors,
    registerPage,
    formRef,
    register,
    handleSubmitForm,
    isLoading,
    isImageLoaded: true,
    handleSubmit: handleSubmit(onSubmit),
  };
}
